import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { useVeri, dosyaSec, dosyaYukle, tarih } from "@/lib/veri";
import { Band, Bos, Buton, Etiket, Hata, Kutu, Sayfa, tabloBas, tabloHucre } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import { ekListeCipi } from "@/lib/ek-reject";
import { siraliYenile } from "@/lib/mrb-toplu";
import type { RaporTuru } from "@/lib/rapor-toplu";
import { BekleyenRaporlar, RaporDonusturucuKutusu, raporSiraIslemi, type RaporListesi } from "./RaporKuyruk";
import { RaporTopluYukleme } from "./RaporTopluYukleme";

/**
 * Muayene doğrulama, riser listesi. Kalem düzeyi girdisi (R{rn}-disposition-input-EN.xlsx, D-015) yüklenmiş
 * riser'lar buradan açılır. Girdisi olmayan, eski komponent düzeyi triyajda bekleyen kalemler
 * kaybolmasın diye alt kutuda listelenir.
 * D-065: Disposition ve Rework raporları buradan toplu yüklenir ("Upload Disposition reports", "Upload Rework reports"); girdisi
 * bekleyen raporlar ve dönüştürücünün durumu listenin üstündeki iki kutudadır (`GET /api/rapor`).
 */

/** Sınıf kodları ham hâliyle okunaksız; ekranda Türkçe karşılıkları gösterilir. */
const SINIF_ETIKET: Record<string, string> = { gecen: "passed", rework: "rework", degisim: cevir("Replacement"), yok_sayilan: "ignored", gundem_disi: cevir("out of scope"), muayene_bekliyor: cevir("inspection pending"), bilinmeyen: "unknown" };

/**
 * D-065: `girdiKaynagi.kaynak` "donusturucu" = disposition girdisini dönüştürücü teslim etti (satırda küçük "converter" etiketi).
 * D-069: `ekAcik` = görünür komponentlerdeki kalemi açılmamış REJECT'li ek doğrulama satırı sayısı (kırmızı çip; triyaj sayfasında açılır).
 */
interface Set { girdiKaynagi?: { kaynak: "elle" | "donusturucu" } | null; rn: string; riserAd: string | null; assemblySn: string | null; dispoTarihi: string | null; komponent: number; tamamlanan: number; bekleyenKalem: number; reworkKalem: number; idariKapanis: number; reworkIslendi: boolean; ekAcik: number }
interface Liste { setler: Set[]; eskiAcik: number }

/** Dönüştürücü durumu canlı görünsün diye kutular bu aralıkla kendini yeniler (söküm listesiyle aynı). */
const YENILEME_MS = 30_000;

export function Dogrulama() {
  const { veri, setVeri, hata, yenile } = useVeri<Liste>("/muayene");
  const nav = useNavigate();
  const [islem, setIslem] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [sonuc, setSonuc] = useState<{ rn: string; ozet: { kalem: number; gorunurKomponent: number; dagilim: Record<string, number> }; uyarilar: string[] } | null>(null);
  /** D-065: açık toplu yükleme penceresi, riseri okunamayan satırda seçilecek riserler, kuyruk işlemi süren kayıt. */
  const [toplu, setToplu] = useState<RaporTuru | null>(null);
  const [rnler, setRnler] = useState<string[]>([]);
  const [siraMesgul, setSiraMesgul] = useState<string | null>(null);
  const [rapor, setRapor] = useState<RaporListesi | null>(null);
  const [simdi, setSimdi] = useState(() => Date.now());

  /**
   * Kutuların sessiz yenilemesi (Sokum.tsx'teki desen): hata ekranı açmaz, sayfanın geri kalanı `/muayene` ile çizilir. Aynı anda
   * uçan yenilemelerden yalnız en son başlatılanın yanıtı yazılır.
   */
  const getir = useMemo(() => siraliYenile(() => api.get<RaporListesi>("/rapor"), setRapor), []);
  const tazele = useCallback(async () => {
    try {
      await getir();
    } catch {
      // Arka plan yenilemesi: bir sonraki turda yeniden denenir.
    }
    setSimdi(Date.now());
  }, [getir]);

  /**
   * Set listesinin sessiz yenilemesi: toplu yükleme penceresi her turdan sonra bunu çağırır. `useVeri`nin `yenile`si hata verince
   * sayfanın yerine hata bandı çizilir ve açık pencere satırlarıyla, sonuçlarıyla, "Retry" düğmeleriyle birlikte kalkar; bu yüzden
   * pencereden gelen yenileme hatayı yutar. Başarısız yenileme `bayat`ı kaldırır, aralıklı tur yeniden dener.
   */
  const setGetir = useMemo(() => siraliYenile(() => api.get<Liste>("/muayene"), setVeri), [setVeri]);
  const bayat = useRef(false);
  const setTazele = useCallback(async () => {
    try {
      await setGetir();
      bayat.current = false;
    } catch {
      bayat.current = true;
    }
  }, [setGetir]);

  useEffect(() => {
    void tazele();
    const t = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void tazele();
      if (bayat.current) void setTazele();
    }, YENILEME_MS);
    return () => window.clearInterval(t);
  }, [tazele, setTazele]);

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;

  const yukle = async () => {
    const f = await dosyaSec(".xlsx");
    if (!f) return;
    setIslem(null);
    setSonuc(null);
    setYukleniyor(true);
    try {
      setSonuc(await dosyaYukle("/muayene/girdi", f));
      await yenile();
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Upload failed"));
    } finally {
      setYukleniyor(false);
    }
  };

  /** Pencereyi açar; riser seçicisi sistemdeki bütün riserlerdir (dosya adından okunamayan satır için), okunamazsa boş kalır. */
  const topluAc = (tur: RaporTuru) => {
    setIslem(null);
    setToplu(tur);
    void api.get<{ riserler: { rn: string }[] }>("/riser-belgeleri").then((v) => setRnler(v.riserler.map((r) => r.rn))).catch(() => {});
  };
  /** Yüklenen rapor seti de, bekleyenler listesini de değiştirir. İkisi de sessizdir: açık pencere hiçbir yenileme hatasında kalkmaz. */
  const hepsiniTazele = () => {
    void setTazele();
    void tazele();
  };

  const sira = async (id: string, i: "ekle" | "cikar") => {
    setIslem(null);
    setSiraMesgul(id);
    try {
      await raporSiraIslemi(id, i);
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Operation failed"));
    } finally {
      setSiraMesgul(null);
      await tazele();
    }
  };

  return (
    <Sayfa
      ust={cevir("2.3 · E-15 Triage")}
      baslik={cevir("Inspection verification")}
      alt={<>{cevir("Post-inspection Triage: which components of this riser are ready to move to assembly, which are waiting for what. Input is extracted from Disposition SURVREP")} <b>{cevir("Item level")}</b> {cevir("database.")}</>}
      aksiyon={<div className="flex flex-wrap gap-2">
        <Buton onClick={() => api.indir("/muayene/sablon.xlsx")}>{cevir("Download empty template")}</Buton>
        <Buton islem="dogrula" tur="birincil" mesgul={yukleniyor} onClick={yukle}>{yukleniyor ? cevir("Reading excel…") : cevir("Upload disposition input (4 sheets)")}</Buton>
        {/* Tek dosyalık yükleme sürerken toplu pencere açılmaz: o yüklemenin yenilemesi hata verirse sayfanın yerine hata bandı çizilir ve açık pencere kalkardı. */}
        <Buton islem="evrak" disabled={yukleniyor} onClick={() => topluAc("disposition")}>{cevir("Upload Disposition reports")}</Buton>
        <Buton islem="dogrula" disabled={yukleniyor} onClick={() => topluAc("rework")}>{cevir("Upload Rework reports")}</Buton>
      </div>}
    >
      <Hata mesaj={islem} />

      {sonuc && (
        <Kutu baslik={cevir`R${sonuc.rn} input uploaded`}>
          <p className="text-sm">{sonuc.ozet.gorunurKomponent} {cevir("component ·")} {sonuc.ozet.kalem} {cevir("Item. Distribution:")} {Object.entries(sonuc.ozet.dagilim).map(([k, n]) => `${SINIF_ETIKET[k] ?? k} ${n}`).join(" · ")}.</p>
          {sonuc.uyarilar.map((u, i) => <Band key={i} tip="not" className="mt-2">{u}</Band>)}
          <div className="mt-3"><Buton kucuk tur="birincil" onClick={() => void nav({ to: "/dogrulama/$rn", params: { rn: sonuc.rn } })}>R{sonuc.rn} {cevir("open Triage")}</Buton></div>
        </Kutu>
      )}

      {rapor && <RaporDonusturucuKutusu d={rapor.donusturucu} simdi={simdi} />}
      {rapor && <BekleyenRaporlar liste={rapor.bekleyenler} mesgul={siraMesgul} islem={(id, i) => void sira(id, i)} />}

      <Kutu baslik={cevir("Item level Triage")} padded={false}>
        <div className="scroll-thin overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead><tr className="border-b border-line"><th className={tabloBas}>RN</th><th className={tabloBas}>Assembly SN</th><th className={tabloBas}>Disposition</th><th className={tabloBas}>{cevir("Component")}</th><th className={tabloBas}>{cevir("Status")}</th><th /></tr></thead>
            <tbody>{veri.setler.map((s) => (
              <tr key={s.rn} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface-2" onClick={() => void nav({ to: "/dogrulama/$rn", params: { rn: s.rn } })}>
                <td className={tabloHucre}><b className="font-mono">R{s.rn}</b></td>
                <td className={tabloHucre}><span className="font-mono text-xs">{s.assemblySn ?? "—"}</span></td>
                <td className={tabloHucre}>{tarih(s.dispoTarihi)}</td>
                <td className={tabloHucre}>
                  <span className="tnum">{s.tamamlanan}/{s.komponent}</span> {cevir("complete")}
                  {/* Noktali ilerleme: her komponent bir nokta, tamamlananlar dolu. Detay sayfasindaki
                      bar ile ayni dili konusur, listede tek bakista "kac kaldi" gorunur. */}
                  {s.komponent > 0 && (
                    <span className="ml-2 inline-flex items-center gap-[3px] align-middle" title={cevir`${s.tamamlanan} / ${s.komponent} components done`}>
                      {Array.from({ length: s.komponent }, (_, i) => (
                        <span key={i} className={`inline-block h-[7px] w-[7px] rounded-full ${i < s.tamamlanan ? "bg-ok" : "bg-surface-3"}`} />
                      ))}
                    </span>
                  )}
                </td>
                <td className={tabloHucre}><span className="flex flex-wrap gap-1">
                  {s.girdiKaynagi?.kaynak === "donusturucu" && <Etiket>{cevir("converter")}</Etiket>}
                  {s.reworkIslendi && <Etiket tone="ok">{cevir("Rework processed")}</Etiket>}
                  {s.reworkKalem > 0 && <Etiket tone="warn">{s.reworkKalem} {cevir("Rework open")}</Etiket>}
                  {s.idariKapanis > 0 && <Etiket tone="hold">{s.idariKapanis} {cevir("Administrative closure")}</Etiket>}
                  {s.bekleyenKalem > 0 && <Etiket tone="bad">{s.bekleyenKalem} {cevir("waiting for Replacement")}</Etiket>}
                  {s.ekAcik > 0 && <Etiket tone="bad">{ekListeCipi(s.ekAcik)}</Etiket>}
                  {s.tamamlanan === s.komponent && <Etiket tone="ok">{cevir("complete")}</Etiket>}
                </span></td>
                <td className={tabloHucre}><Link to="/dogrulama/$rn" params={{ rn: s.rn }} className="text-sm font-semibold text-orange" onClick={(e) => e.stopPropagation()}>{cevir("Open")}</Link></td>
              </tr>
            ))}
              {!veri.setler.length && <tr><td colSpan={6}><Bos>{cevir("Item level input not uploaded yet.")}</Bos></td></tr>}</tbody>
          </table>
        </div>
      </Kutu>

      <Band tip="bilgi">{cevir("Upload one or many Disposition or Rework report PDFs at once with Upload Disposition reports and Upload Rework reports, with or without their input excels. The riser is read from the file name; if it cannot be read, you select the riser on that file's row. A PDF uploaded with its excel is read at once; a PDF uploaded alone joins the converter queue and is listed under Reports waiting for their input until its excel arrives. For a bulk-uploaded Disposition report the earlier Field steps that are missing are recorded as not performed and Receiving is skipped. A PDF that carries several risers goes through the single-riser doors: Upload Disposition on the Field list and Upload Rework Scope on the riser's page.")}</Band>

      <Band tip="bilgi">{cevir("Input excel is the disposition input produced by the standard tool (R{rn}-disposition-input-EN.xlsx), four sheets: RISER · COMPONENTS · ITEMS · MAPPING_REPORT. If MAPPING_REPORT has a")} <b>{cevir("WARNING")}</b> {cevir("row, the file is rejected; a warning means an item or component the tool has no rule for yet, and it is resolved in the tool, not here. CONFLICT rows enter and are marked as ID Conflict on screen.")} <b>{cevir("MRB content excel is not uploaded here")}</b>{cevir(", it is uploaded to the page of the relevant MRB record on the MRB intake screen.")}</Band>

      {veri.eskiAcik > 0 && (
        <Kutu baslik={cevir("Risers without Item level input")}>
          <p className="text-base text-ink-2">{cevir("In old component level Triage")} <b>{veri.eskiAcik}</b> {cevir("there is an open Item. Those risers are managed there until their Item level Disposition database is generated.")}</p>
          <div className="mt-3"><Link to="/dogrulama/eski"><Buton kucuk>{cevir("Go to component level Triage")}</Buton></Link></div>
        </Kutu>
      )}
      {toplu && <RaporTopluYukleme key={toplu} tur={toplu} rnler={rnler} onKapat={() => { setToplu(null); hepsiniTazele(); }} onBitti={hepsiniTazele} />}
    </Sayfa>
  );
}
